import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

// Fixed, literal class lookup (never string-built) so Tailwind's content
// scan always sees the full class names — the 4 door hub pages previously
// derived these via `color.replace('border-','bg-').replace('-200','-100')`
// string hacks, which is fragile under JIT purge. Each key mirrors a door
// from tailwind.config.js's `door.*` tokens (used for large gradient
// surfaces elsewhere); here we use the matching light/700 pair for small
// icon chips and links, which the door tokens don't carry shades for.
const ACCENTS = {
  primary: { chip: 'bg-rose-100 text-rose-600', link: 'text-rose-600 hover:text-rose-700' },
  secondary: { chip: 'bg-brand-100 text-brand-600', link: 'text-brand-600 hover:text-brand-700' },
  tertiary: { chip: 'bg-indigo-100 text-indigo-600', link: 'text-indigo-600 hover:text-indigo-700' },
  chance: { chip: 'bg-amber-100 text-amber-600', link: 'text-amber-600 hover:text-amber-700' },
  brand: { chip: 'bg-brand-100 text-brand-600', link: 'text-brand-600 hover:text-brand-700' },
};

// A summary card (icon, title, meta, description, optional tags) that
// reveals arbitrary detail content on click. Used by all 4 door hub pages
// instead of each hand-rolling its own near-identical expand/collapse card.
export const ExpandableCard = ({
  icon: Icon,
  title,
  meta,
  description,
  tags,
  accent = 'brand',
  // Optional literal-class override for the icon chip (e.g. a caller listing
  // several distinct programmes that benefit from being told apart at a
  // glance, like SecondChanceEducationHub's pathways list). Must be passed
  // as full literal classnames from the caller, never built dynamically.
  iconChipClassName,
  defaultExpanded = false,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const a = ACCENTS[accent] || ACCENTS.brand;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-start gap-4">
        {Icon && (
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${iconChipClassName || a.chip}`}>
            <Icon className="w-7 h-7" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
          {meta && <p className="text-sm text-gray-500">{meta}</p>}
        </div>
      </div>

      {description && <p className="text-gray-600 mt-4">{description}</p>}

      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {tags.map((tag, idx) => (
            <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      )}

      {children && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={`font-medium text-sm mt-3 flex items-center gap-1 ${a.link}`}
        >
          {expanded ? 'Show Less' : 'Learn More'}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      )}

      {expanded && children && (
        <div className="mt-4 pt-4 border-t border-gray-100">{children}</div>
      )}
    </div>
  );
};

// Convenience: a bullet feature list for the common case (Primary's
// StageCard / Secondary's SchoolTypeCard), so callers don't hand-roll <ul>.
export const FeatureList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
        <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
        {item}
      </li>
    ))}
  </ul>
);

export default ExpandableCard;
